/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import React, { useState, useEffect, useRef } from "react";
import { fetchListOfStatusesAvailableRequest } from "@Features/reviews/api/reviews.api";
import IconComponent from "@CubeComponents/icons";
import { changeResourceStatusInReview, getReviewsListForEntity, getStatusBtnStyle } from "./ReviewsHelper";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ChangeFileStatusModal from "./ChangeFileStatusModal";
import { getReviewUrl } from "@Features/projects/utils/projectUrls";
import _ from "lodash";
import Text from "@CubeComponents/html/Text/Text";
import {
  ReviewsListForEntityProps,
  Review,
  ApprovalStage,
  SignatureModalState,
  SignatureModalData,
  DropdownItemTemplateArgs,
} from "./reviews.interfaces";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";

function ReviewsListForEntity({
  resource,
  spaceId,
  includeDefaultReviews = false,
  skipPopUp = false,
  confirmForNonDefault = false,
  successMessage,
  onRequestStatusConfirm,
  canEditDefaultStatus = true,
}: Readonly<ReviewsListForEntityProps>) {
  const { t } = useTranslation();
  const defaultState: SignatureModalState = { show: false, data: null };
  const [reviewsList, setReviewsList] = useState<Review[]>([]);
  const [signatureModal, setSignatureModal] = useState<SignatureModalState>(defaultState);
  const [statusOptionsMap, setStatusOptionsMap] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);
  const statusRefs = useRef<any[]>([]);

  const dispatch = useDispatch();

  const closeSignatureModal = () => {
    setSignatureModal(defaultState);
    fetchReviewsListForEntity();
  };

  const openReview = (e: React.MouseEvent<HTMLElement>) => {
    const projectId = e.currentTarget.dataset.projectid ?? "";
    const reviewId = e.currentTarget.dataset.reviewid ?? "";
    const spaceId = e.currentTarget.dataset.spaceid ?? "";

    const url = getReviewUrl(projectId, spaceId, reviewId);
    window.open(url, "_blank");
  };

  const saveStatusWithoutConfirmation = async ({ statusTo, reviewId, resources }) => {
    const data = {
      statusTo: statusTo._id,
      reviewId,
      resources,
    };

    const response = await changeResourceStatusInReview(data);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: successMessage ?? t("Status has been updated successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      // Refresh this list so the row reflects the new status (mirrors the confirmation-modal path).
      fetchReviewsListForEntity();
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const entityStatusChanged = (
    newValue: any,
    previousStatus: any,
    reviewId: string,
    approvalStages: ApprovalStage[],
    isDefaultReview = false,
  ) => {
    const statusTo = newValue;
    const statusFrom = previousStatus || {
      _id: null,
      name: null,
      category: null,
    };

    const currentStage = _.find(approvalStages, {
      clearanceStatus: "in_progress",
    });
    const requireMandatoryComment = currentStage
      ? currentStage.requireMandatoryComment.enabled && currentStage.requireMandatoryComment.values.includes(4)
      : false;

    // For issues (confirmForNonDefault), the default review saves directly while any other review
    // must go through the confirmation popup (R7). Otherwise fall back to the shared skipPopUp flag.
    const skipConfirmation = confirmForNonDefault ? isDefaultReview : skipPopUp;

    if (skipConfirmation) {
      saveStatusWithoutConfirmation({ statusTo, reviewId, resources: [resource] });
      return;
    }

    const confirmData: SignatureModalData = {
      reviewId,
      statusTo,
      statusFrom,
      resources: [resource],
      type: "standardTransition",
      requireMandatoryComment,
    };

    // When the caller owns the confirmation (e.g. IssueStatusPopover renders this list inside a
    // popover), hand off the modal so it can close the popover first — otherwise the popover and the
    // modal stack. Without a handler we render the modal inline (Files/Tasks).
    if (onRequestStatusConfirm) {
      onRequestStatusConfirm(confirmData);
    } else {
      setSignatureModal({ show: true, data: confirmData });
    }
  };

  const fetchStatusOptionsForReviews = async (reviews: Review[]) => {
    const newStatusOptionsMap: Record<string, any[]> = {};
    await Promise.all(
      reviews.map(async (review: Review) => {
        try {
          const statusResponse = await fetchListOfStatusesAvailableRequest({
            value: { params: { reviewId: review.reviewId, resource } },
          } as any);
          if (statusResponse.data?.result) {
            newStatusOptionsMap[review.reviewId!] = statusResponse.data.result;
          }
        } catch (e) {
          console.error("Error fetching status options:", e);
          Promise.reject(e);
        }
      }),
    );
    setStatusOptionsMap(newStatusOptionsMap);
  };

  const fetchReviewsListForEntity = async () => {
    setLoading(true);
    try {
      const params = { resource, spaceId, includeDefaultReviews };
      const response = await getReviewsListForEntity({ value: { params } });

      if (response.data.status) {
        setReviewsList(response.data.result!);
        await fetchStatusOptionsForReviews(response.data.result!);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviewsListForEntity();
  }, [resource.fileVersionId, resource.taskId, resource.issueId, resource.resourceType]);

  const _dropdownItemTemplate = (args: DropdownItemTemplateArgs) => {
    const style = getStatusBtnStyle(args.category);

    return (
      <div className="py-2 px-3">
        <span style={style} className="rounded py-1 px-4 text-sm text-white">
          {args.name}
        </span>
      </div>
    );
  };

  return (
    <>
      {loading && !reviewsList.length ? (
        <div className="py-4 text-sm text-text_light">{t("Loading...")}</div>
      ) : reviewsList?.length ? (
        <div className="flex flex-col gap-6 overflow-auto py-2">
          {reviewsList?.length
            ? reviewsList.map((review: any, index: number) => {
                const { name, status, spaceId, projectId, reviewId, isDefaultReview, serialId, approvalStages } =
                  review;

                if (!statusRefs.current[index]) {
                  statusRefs.current[index] = React.createRef<any>();
                }

                const statusStyle = status?.category ? getStatusBtnStyle(status.category) : undefined;

                const statusDropdownEl = (
                  <AutoComplete
                    options={statusOptionsMap[reviewId] || []}
                    disabled={isDefaultReview && !canEditDefaultStatus}
                    valueField="_id"
                    width={"100%"}
                    minWidth={0}
                    getOptionLabel={(option: any) => option?.name || ""}
                    value={(statusOptionsMap[reviewId] || []).find((opt: any) => opt._id === status?._id) || null}
                    onChange={(_event: any, newValue: any) => {
                      if (newValue) {
                        entityStatusChanged(newValue, status, reviewId, approvalStages, isDefaultReview);
                      }
                    }}
                    renderOption={(props: any, option: any) => (
                      <li {...props}>
                        <div className="py-2 w-full">
                          <Text
                            style={getStatusBtnStyle(option.category)}
                            className="rounded py-1 px-4 text-sm text-white"
                          >
                            {option.name}
                          </Text>
                        </div>
                      </li>
                    )}
                    sx={
                      statusStyle
                        ? {
                            "& .MuiInputBase-root": {
                              background: `${statusStyle.backgroundColor} !important`,
                              borderRadius: "0.25rem",
                              fontWeight: 600,
                            },
                            "& .MuiOutlinedInput-input": {
                              background: "transparent !important",
                              color: "#fff !important",
                              fontSize: "0.875rem",
                              fontWeight: 600,
                            },
                            "& .MuiSvgIcon-root": { color: "#fff" },
                            "& .MuiOutlinedInput-notchedOutline": { border: "none !important" },
                          }
                        : undefined
                    }
                    slotProps={{
                      listbox: { sx: { padding: 0 } },
                      popper: {
                        placement: "bottom-start",
                        style: { width: "12rem" },
                      },
                    }}
                    renderInput={(params: any) => <TextFieldComponent {...params} name="status" />}
                  />
                );

                return (
                  <div key={reviewId} className="flex flex-row items-center gap-3">
                    {/* Default review -> info icon (non-navigable); others -> #serialId that swaps to the
                        open-review icon on hover and navigates to that review. */}
                    <div className="group flex w-[2rem] shrink-0 items-center">
                      {isDefaultReview ? (
                        <IconComponent
                          name="details"
                          toolTipContent={t(
                            "This is the default review for the issue. The status can be changed but the review can not be accessed",
                          )}
                          position="top"
                        />
                      ) : (
                        <>
                          <span className="text-sm group-hover:hidden">{`#${serialId}`}</span>
                          <Box
                            data-reviewid={reviewId}
                            data-spaceid={spaceId}
                            data-projectid={projectId}
                            onClick={openReview}
                            className="hidden cursor-pointer items-center group-hover:flex"
                          >
                            <IconComponent name="openInViewer" className="w-5 h-5" />
                          </Box>
                        </>
                      )}
                    </div>
                    {/* Text shows the full review name as a tooltip when it overflows. */}
                    <div className="flex flex-1 min-w-0 gap-3 items-center">
                      <Text className="min-w-0 flex-2 text-sm font-semibold" tooltipContent={name}>
                        {name}
                      </Text>
                      <div className="min-w-0 flex-3">{statusDropdownEl}</div>
                    </div>
                  </div>
                );
              })
            : undefined}
        </div>
      ) : null}
      {signatureModal.show && (
        <ChangeFileStatusModal
          {...signatureModal.data}
          closeModal={closeSignatureModal}
          successMessage={successMessage}
        />
      )}
    </>
  );
}

export default ReviewsListForEntity;
